import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  Image,
  Dimensions,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { Ionicons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { RootStackParamList } from '../../types/navigation';
import { useTheme } from '../../hooks/useTheme';
import DetailContainer from '../../components/shared/DetailContainer';
import { Button } from '../../components';

type SmartWardrobeDetailNavigationProp =
  NativeStackNavigationProp<RootStackParamList>;

const { width: screenWidth } = Dimensions.get('window');

const SmartWardrobeDetailScreen = () => {
  const navigation = useNavigation<SmartWardrobeDetailNavigationProp>();
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const handleStartWardrobe = () => {
    navigation.navigate('MainTabs', { initialTab: 'SmartWardrobe' });
  };

  const keyFeatures = [
    {
      icon: 'library-outline',
      title: 'Digital Closet',
      description:
        'Digitize your entire wardrobe with photos and detailed color information',
    },
    {
      icon: 'sparkles-outline',
      title: 'AI Outfit Suggestions',
      description:
        'Get personalized outfit recommendations based on your color palette and style',
    },
    {
      icon: 'color-palette-outline',
      title: 'Color Coordination',
      description:
        'Ensure perfect color harmony in every outfit with our intelligent matching system',
    },
    {
      icon: 'analytics-outline',
      title: 'Style Analytics',
      description:
        'Track your style patterns and discover new outfit combinations',
    },
    {
      icon: 'calendar-outline',
      title: 'Occasion Planning',
      description: 'Plan outfits for specific events, weather, and activities',
    },
    {
      icon: 'trending-up-outline',
      title: 'Trend Integration',
      description:
        'Stay current with personalized trend recommendations that suit your style',
    },
  ];

  const steps = [
    {
      step: '01',
      title: 'Add Your Clothes',
      description: 'Photo your wardrobe items and categorize them',
    },
    {
      step: '02',
      title: 'AI Analysis',
      description: 'Our AI analyzes colors and style compatibility',
    },
    {
      step: '03',
      title: 'Get Suggestions',
      description: 'Receive personalized outfit recommendations',
    },
  ];

  const benefits = [
    'Never wonder "what to wear" again',
    'Maximize your existing wardrobe',
    'Shop smarter with gap analysis',
    'Save time getting dressed',
    'Discover new outfit combinations',
    'Build a cohesive style identity',
  ];

  return (
    <DetailContainer style={{ backgroundColor: colors.background }}>
      <ScrollView
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
      >
        {/* Hero Section */}
        <LinearGradient
          colors={[colors.helpCenterText, colors.linearGradient4]}
          style={styles.heroSection}
        >
          <View style={styles.heroContent}>
            <Text style={[styles.heroTitle, { color: colors.background }]}>
              Your Digital Closet, Powered by AI
            </Text>
            <Text style={styles.heroSubtitle}>
              Transform your wardrobe into a smart, organized system that
              suggests perfect outfits every day
            </Text>
          </View>
        </LinearGradient>

        {/* Product Demo Images */}
        <View style={styles.demoSection}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            See Your Digital Wardrobe
          </Text>
          <View style={styles.demoGrid}>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=9' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text style={[styles.demoLabel, { color: colors.text }]}>
                Digital Closet
              </Text>
            </View>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=10' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text style={[styles.demoLabel, { color: colors.text }]}>
                Outfit Suggestions
              </Text>
            </View>
            <View style={styles.demoImageContainer}>
              <Image
                source={{ uri: 'https://picsum.photos/300/500?random=11' }}
                style={styles.demoImage}
                resizeMode="cover"
              />
              <Text style={[styles.demoLabel, { color: colors.text }]}>
                Style Analytics
              </Text>
            </View>
          </View>
        </View>

        {/* How It Works */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            How It Works
          </Text>
          <View style={styles.stepsContainer}>
            {steps.map((step, index) => (
              <View key={index} style={styles.stepItem}>
                <View
                  style={[
                    styles.stepNumber,
                    { backgroundColor: colors.helpCenterText },
                  ]}
                >
                  <Text
                    style={[
                      styles.stepNumberText,
                      { color: colors.background },
                    ]}
                  >
                    {step.step}
                  </Text>
                </View>
                <View style={styles.stepContent}>
                  <Text style={[styles.stepTitle, { color: colors.text }]}>
                    {step.title}
                  </Text>
                  <Text
                    style={[
                      styles.stepDescription,
                      { color: colors.secondary },
                    ]}
                  >
                    {step.description}
                  </Text>
                </View>
              </View>
            ))}
          </View>
        </View>

        {/* Key Features */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Smart Features
          </Text>
          <View style={styles.featuresGrid}>
            {keyFeatures.map((feature, index) => (
              <View
                key={index}
                style={[
                  styles.featureCard,
                  {
                    backgroundColor: colors.background,
                    shadowColor: colors.text,
                  },
                ]}
              >
                <View
                  style={[
                    styles.featureIcon,
                    { backgroundColor: 'rgba(245, 158, 11, 0.1)' },
                  ]}
                >
                  <Ionicons
                    name={feature.icon as any}
                    size={24}
                    color={colors.helpCenterText}
                  />
                </View>
                <Text style={[styles.featureTitle, { color: colors.text }]}>
                  {feature.title}
                </Text>
                <Text
                  style={[
                    styles.featureDescription,
                    { color: colors.secondary },
                  ]}
                >
                  {feature.description}
                </Text>
              </View>
            ))}
          </View>
        </View>

        {/* Benefits Section */}
        <View style={styles.section}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Transform Your Wardrobe Experience
          </Text>
          <View
            style={[
              styles.benefitsCard,
              { backgroundColor: colors.card, shadowColor: colors.text },
            ]}
          >
            <Image
              source={{ uri: 'https://picsum.photos/400/250?random=12' }}
              style={styles.benefitsImage}
              resizeMode="cover"
            />
            <View style={styles.benefitsContent}>
              <Text style={[styles.benefitsTitle, { color: colors.text }]}>
                Effortless Style Every Day
              </Text>
              <View style={styles.benefitsList}>
                {benefits.map((benefit, index) => (
                  <View key={index} style={styles.benefitItem}>
                    <Ionicons
                      name="checkmark-circle"
                      size={16}
                      color={colors.helpCenterText}
                    />
                    <Text style={[styles.benefitText, { color: colors.text }]}>
                      {benefit}
                    </Text>
                  </View>
                ))}
              </View>
            </View>
          </View>
        </View>

        {/* Smart Features Showcase */}
        <View style={[styles.section, { backgroundColor: colors.card }]}>
          <Text style={[styles.sectionTitle, { color: colors.text }]}>
            Intelligent Wardrobe Management
          </Text>
          <View style={styles.showcaseGrid}>
            <View
              style={[
                styles.showcaseCard,
                {
                  backgroundColor: colors.background,
                  shadowColor: colors.text,
                },
              ]}
            >
              <Ionicons
                name="shirt-outline"
                size={40}
                color={colors.helpCenterText}
              />
              <Text style={[styles.showcaseTitle, { color: colors.text }]}>
                Item Recognition
              </Text>
              <Text
                style={[
                  styles.showcaseDescription,
                  { color: colors.secondary },
                ]}
              >
                AI automatically categorizes and tags your clothing items
              </Text>
            </View>
            <View
              style={[
                styles.showcaseCard,
                { backgroundColor: colors.background },
              ]}
            >
              <Ionicons
                name="sunny-outline"
                size={40}
                color={colors.helpCenterText}
              />
              <Text style={[styles.showcaseTitle, { color: colors.text }]}>
                Weather Integration
              </Text>
              <Text
                style={[
                  styles.showcaseDescription,
                  { color: colors.secondary },
                ]}
              >
                Get outfit suggestions based on daily weather conditions
              </Text>
            </View>
            <View
              style={[
                styles.showcaseCard,
                { backgroundColor: colors.background },
              ]}
            >
              <Ionicons
                name="stats-chart-outline"
                size={40}
                color={colors.helpCenterText}
              />
              <Text style={[styles.showcaseTitle, { color: colors.text }]}>
                Usage Analytics
              </Text>
              <Text
                style={[
                  styles.showcaseDescription,
                  { color: colors.secondary },
                ]}
              >
                Track which items you wear most and discover forgotten pieces
              </Text>
            </View>
            <View
              style={[
                styles.showcaseCard,
                { backgroundColor: colors.background },
              ]}
            >
              <Ionicons
                name="bag-outline"
                size={40}
                color={colors.helpCenterText}
              />
              <Text style={[styles.showcaseTitle, { color: colors.text }]}>
                Shopping Assistant
              </Text>
              <Text
                style={[
                  styles.showcaseDescription,
                  { color: colors.secondary },
                ]}
              >
                Identify wardrobe gaps and get smart shopping recommendations
              </Text>
            </View>
          </View>
        </View>

        {/* CTA Section */}
        <View style={styles.ctaSection}>
          <LinearGradient
            colors={[colors.helpCenterText, colors.linearGradient4]}
            style={styles.ctaGradient}
          >
            <Text style={[styles.ctaTitle, { color: colors.background }]}>
              Ready to Organize Your Wardrobe?
            </Text>
            <Text style={styles.ctaSubtitle}>
              Start building your smart digital closet today
            </Text>
            <Button
              title="Create Smart Wardrobe"
              onPress={handleStartWardrobe}
              variant="primary"
              style={{
                ...styles.ctaButton,
                backgroundColor: colors.background,
              }}
              textStyle={{
                ...styles.ctaButtonText,
                color: colors.helpCenterText,
              }}
              hoverColor={colors.border}
              trackClick={true}
              trackingLocation="smart-wardrobe-detail-screen"
              trackingData={{
                button_name: 'Create Smart Wardrobe',
                button_type: 'create_smart_wardrobe',
                screen: 'smart_wardrobe_detail',
              }}
            />
          </LinearGradient>
        </View>
      </ScrollView>
    </DetailContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingBottom: 10,
  },
  backButton: {
    padding: 8,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  placeholder: {
    width: 40,
  },
  scrollView: {
    flex: 1,
  },
  heroSection: {
    paddingHorizontal: 20,
    paddingVertical: 40,
    alignItems: 'center',
  },
  heroContent: {
    maxWidth: 500,
    alignItems: 'center',
  },
  heroTitle: {
    fontSize: 28,
    textAlign: 'center',
    marginBottom: 16,
  },
  heroSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    lineHeight: 24,
  },
  demoSection: {
    paddingVertical: 30,
  },
  demoGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 16,
  },
  demoImageContainer: {
    alignItems: 'center',
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
  },
  demoImage: {
    width: '100%',
    height: 300,
    borderRadius: 12,
    marginBottom: 8,
  },
  demoLabel: {
    fontSize: 14,
    fontWeight: '500',
  },
  section: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  sectionTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 24,
  },
  stepsContainer: {
    gap: 20,
  },
  stepItem: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stepNumber: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: 16,
  },
  stepNumberText: {
    fontSize: 16,
    fontWeight: '600',
  },
  stepContent: {
    flex: 1,
  },
  stepTitle: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  stepDescription: {
    fontSize: 14,
    lineHeight: 20,
  },
  featuresGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'center',
  },
  featureCard: {
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
    padding: 16,
    borderRadius: 12,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  featureIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 12,
  },
  featureTitle: {
    fontSize: 14,
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: 8,
  },
  featureDescription: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 16,
  },
  benefitsCard: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 4,
  },
  benefitsImage: {
    width: '100%',
    height: 200,
  },
  benefitsContent: {
    padding: 20,
  },
  benefitsTitle: {
    fontSize: 20,
    fontWeight: '600',
    marginBottom: 16,
  },
  benefitsList: {
    gap: 8,
  },
  benefitItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  benefitText: {
    fontSize: 14,
    marginLeft: 8,
  },
  showcaseGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 16,
    justifyContent: 'center',
  },
  showcaseCard: {
    width: (screenWidth - 60) / 2,
    maxWidth: 160,
    padding: 20,
    borderRadius: 12,
    alignItems: 'center',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
  },
  showcaseTitle: {
    fontSize: 16,
    fontWeight: '600',
    textAlign: 'center',
    marginTop: 12,
    marginBottom: 8,
  },
  showcaseDescription: {
    fontSize: 12,
    textAlign: 'center',
    lineHeight: 16,
  },
  ctaSection: {
    paddingHorizontal: 20,
    paddingVertical: 30,
  },
  ctaGradient: {
    borderRadius: 16,
    paddingVertical: 40,
    paddingHorizontal: 20,
    alignItems: 'center',
  },
  ctaTitle: {
    fontSize: 24,
    fontWeight: '700',
    textAlign: 'center',
    marginBottom: 8,
  },
  ctaSubtitle: {
    fontSize: 16,
    color: 'rgba(255, 255, 255, 0.9)',
    textAlign: 'center',
    marginBottom: 24,
  },
  ctaButton: {
    paddingVertical: 16,
    paddingHorizontal: 32,
    borderRadius: 12,
  },
  ctaButtonText: {
    fontSize: 16,
    fontWeight: '600',
  },
});

export default SmartWardrobeDetailScreen;
